import { Link } from "react-router";

import { Blocks } from "@/components/cms/Blocks";
import { CtaBand } from "@/components/cms/CtaBand";
import { PageSections, type SectionProps, type SectionRenderers } from "@/components/cms/PageSections";
import { LocationRecord, chosenFocalPoint, linkSlot } from "@/components/cms/SectionRenderer";
import { MediaFigure } from "@/components/ui/MediaFigure";
import { PageHeader } from "@/components/ui/PageHeader";
import { Reveal } from "@/components/ui/Reveal";
import { Section } from "@/components/ui/Section";
import { useMediaByKey } from "@/content-context";
import { reliefResilience } from "@/data/media";
import { buildMetadata } from "@/lib/seo";
import { imageReveal } from "@/lib/motion";
import { pageLoader } from "@/page-data";

/**
 * Where we work.
 *
 * No longer an item in the main menu: the homepage carries the places directly
 * beneath the programmes and links here for the record of each one. The page
 * itself is unchanged in what it says, and every place on it, with the dated
 * work recorded there, is editable under Pages → Where we work.
 */

export const loader = pageLoader("where-we-work");

export const meta = () =>
  buildMetadata({
    title: "Where we work",
    description:
      "The places named in HISCUS project reporting: Bethanchowk, Sarangkot, Thapathali, Bhaktapur, Ropeway Sadak, Pokhara and Chitwan, with the documented work in each.",
    path: "/where-we-work",
  });

function IntroSection({ section }: SectionProps) {
  const byKey = useMediaByKey();
  const image = section.mediaKey ? byKey(section.mediaKey) : reliefResilience[2];

  return (
    <Section>
      <div className="grid items-start gap-14 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:gap-20">
        <Reveal>
          {section.title ? <h2 style={{ fontSize: "var(--step-h2)" }}>{section.title}</h2> : null}
          <Blocks blocks={section.body} className="mt-6" />
          {section.texts?.note ? (
            <p className="mt-10 border-l-2 border-sky pl-5 text-[0.9375rem] leading-relaxed text-muted">{section.texts.note}</p>
          ) : null}
        </Reveal>

        {image ? (
          <Reveal variants={imageReveal}>
            <MediaFigure
              item={image}
              ratio="4 / 3"
              sizes="(min-width: 1024px) 46vw, 100vw"
              radius={24}
              objectPosition={chosenFocalPoint(section.settings?.focalPoint)}
            />
          </Reveal>
        ) : null}
      </div>
    </Section>
  );
}

function PlacesSection({ section }: SectionProps) {
  return <LocationRecord section={section} />;
}

function ClosingSection({ section }: SectionProps) {
  const browse = linkSlot(section, 2);
  return (
    <CtaBand section={{ ...section, settings: { ...section.settings, tone: "midnight" } }} primary="light">
      {browse ? (
        <p className="mt-14 text-[0.875rem] text-white/50">
          {section.texts?.footnote ? `${section.texts.footnote} ` : null}
          <Link to={browse.href} className="link-underline text-sky">
            {browse.label}
          </Link>
          .
        </p>
      ) : null}
    </CtaBand>
  );
}

export const whereWeWorkSections: SectionRenderers = {
  "where-we-work.intro": IntroSection,
  "where-we-work.places": PlacesSection,
  "where-we-work.cta": ClosingSection,
};

export function WhereWeWorkPage() {
  return (
    <>
      <PageHeader
        eyebrow="Geography"
        title="Seven places, and what happened in each"
        lead="HISCUS works in a small number of communities repeatedly rather than in many communities once. These are the places its own reporting names. Nothing is extrapolated from a single visit."
        trail={[
          { name: "Home", href: "/" },
          { name: "Where we work", href: "/where-we-work" },
        ]}
      />
      <PageSections renderers={whereWeWorkSections} />
    </>
  );
}
